// Examiner ElasticSearch

import "whatwg-fetch";
import { all } from "awaity/esm";
import { optionsGen } from "../helpers/api";
import { toESQuery, toESDateHistogramQuery, toESGroupByQuery, toESDoubleGroupByQuery, toESTripleGroupByQuery } from "../helpers/query";
import { toTitleCase } from "../helpers/converters";
import { API_URL } from "../config";

const AUTH = false;
export const PROSECUTION_ES_URL = `${API_URL}/patents/prosecution/_search`;

// Default filter applied to all prosecution requests
const DEFAULT_FILTER = { application_type: "utility", filing_date: "2000-01-01--" };

export function getEntityApplications(name, field) {
  return fetch(
    PROSECUTION_ES_URL,
    optionsGen(
      "POST",
      toESQuery({
        ...DEFAULT_FILTER,
        [field]: `"${name}"`, // surround name with quotes to initiate exact matching search
        sort: "-filing_date",
        limit: 10
      })
    )
  ).then(resp => resp.json());
}

export function getEntityApplicationsByDate(query) {
  return all([
    fetch(
      PROSECUTION_ES_URL,
      optionsGen(
        "POST",
        toESDateHistogramQuery("patent_issue_date", "year", {
          ...DEFAULT_FILTER,
          [query.field]: `"${query.name}"`
        })
      )
    ).then(resp => resp.json()),
    fetch(
      PROSECUTION_ES_URL,
      optionsGen(
        "POST",
        toESDateHistogramQuery("abandon_date", "year", {
          ...DEFAULT_FILTER,
          [query.field]: `"${query.name}"`
        })
      )
    ).then(resp => resp.json())
  ]).then(([resID, resAD]) => {
    return [
      {
        k: "Issued",
        v: resID.aggregations.patent_issue_date.buckets.map(b => ({ k: b.key_as_string.substring(0, 4), v: b.doc_count }))
      },
      {
        k: "Abandoned",
        v: resAD.aggregations.abandon_date.buckets.map(b => ({ k: b.key_as_string.substring(0, 4), v: b.doc_count }))
      }
    ];
  });
}

export function getEntityAppCounts(name, field) {
  const query = toESGroupByQuery("disposal_type", {
    ...DEFAULT_FILTER,
    [field]: `"${name}"`
  });
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", query))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      json.aggregations.disposal_type.buckets.forEach(e => (res[e.key] = e.doc_count));

      return res;
    });
}

export function getTopEntitiesByAllowanceRate(field1, top = 30, query = {}, field2 = "disposal_type") {
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", toESDoubleGroupByQuery(field1, field2, { ...DEFAULT_FILTER, ...query }, top * 2)))
    .then(resp => resp.json())
    .then(json => {
      const getAllowanceRate = arr => {
        let issuedCnt = 0;
        let abandonedCnt = 0;
        arr.forEach(o => {
          if (o.key === "Issued") {
            issuedCnt = o.doc_count;
          }
          if (o.key === "Abandoned") {
            abandonedCnt = o.doc_count;
          }
        });
        return issuedCnt + abandonedCnt == 0 ? 0 : issuedCnt / (issuedCnt + abandonedCnt);
      };

      const results = json.aggregations[field1].buckets
        .map(o => ({ key: o.key, count: o.doc_count, allowanceRate: getAllowanceRate(o[field2].buckets) }))
        .sort((small, large) => large.allowanceRate - small.allowanceRate)
        .map((o, i) => ({ ...o, rank: i + 1 }));

      return results.slice(0, top);
    });
}

export function getTopLawFirmsByLPIX(top = 30, query = {}) {
  const q = toESQuery({ ...DEFAULT_FILTER, ...query }, [], 0);
  q.aggs = {
    "law_firm": {
      "terms": {
        "field": "law_firm",
        "size": 200
      },
      "aggs": {
        "count_issued": {
          "filter": {
            "term": {
              "disposal_type": "Issued"
            }
          }
        },
        "count_abandoned": {
          "filter": {
            "term": {
              "disposal_type": "Abandoned"
            }
          }
        },
        "sum_brix": {
          "sum": {
            "field": "brix"
          }
        },
        "sum_pendency": {
          "sum": {
            "field": "days_til_issue"
          }
        }
      }
    }
  };
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", q, AUTH))
    .then(resp => resp.json())
    .then(json => {
      let scoreMax = 0.1e-10; // to avoid division by zero
      const results = json.aggregations.law_firm.buckets
        .map(({ key, count_issued, count_abandoned, sum_brix, sum_pendency }) => {
          const total = count_issued.doc_count + count_abandoned.doc_count;
          if (total > 0) {
            const allowance_rate = count_issued.doc_count / total;
            const pendency = sum_pendency.value ? sum_pendency.value / total / 365 : 2; // avg 2 years if missing
            const narrowness = sum_brix.value ? sum_brix.value / total : 0.5; // avg 0.5 if missing
            // LPIX score, more successful and faster to issue broader patents, the better the law firm
            // a great history of successes is the dominant factor, so the most significant part is allowance rate with additional weight towards total number, but not too much (hence the log2)
            // raw brix score actually measures narrowness, so it has to be inverted, and reduce the significance of narrowness by applying a square root (or log) to compress its range
            // the shorter pendency the better, so it has to be inverted too
            const score = (Math.log2(total) * allowance_rate) / Math.sqrt(pendency) / Math.sqrt(narrowness);
            if (score > scoreMax) scoreMax = score;
            return {
              lf: key,
              t: total,
              n: narrowness,
              p: pendency,
              ar: allowance_rate,
              s: score,
            };
          }
          return {
            lf: key,
            t: 0,
            n: 0,
            p: 0,
            ar: 0,
            s: 0,
          };
        })
        .map(({ lf, t, n, p, ar, s }) => ({ lf, t, n, p, ar, s: s / scoreMax }))
        .sort((a, b) => b.s - a.s);
      return results;
    })
    .then(results => results
      .map((o, i) => ({ ...o, key: o.lf, lpix: o.s, count: o.t, rank: i + 1 }))
      .slice(0, top));
}

export function getEntityAppCountsByOneCondition(name, condition = "examiner_art_unit", byArtUnit = false, field) {
  const query = byArtUnit
    ? toESTripleGroupByQuery(
      "examiner_art_unit",
      condition,
      "disposal_type",
      {
        ...DEFAULT_FILTER,
        [field]: `"${name}"`
      },
      500
    )
    : toESDoubleGroupByQuery(
      condition,
      "disposal_type",
      {
        ...DEFAULT_FILTER,
        [field]: `"${name}"`
      },
      500
    );
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", query))
    .then(resp => resp.json())
    .then(json => {
      if (byArtUnit) {
        let res = {};
        json.aggregations.examiner_art_unit.buckets.forEach(b => {
          res[b.key] = {};
          b[condition].buckets.forEach(bib => {
            res[b.key][bib.key] = {};
            bib.disposal_type.buckets.forEach(bibib => (res[b.key][bib.key][bibib.key] = bibib.doc_count));
          });
        });
        return res;
      } else {
        let res = {};
        json.aggregations[condition].buckets.forEach(b => {
          res[b.key] = {};
          b.disposal_type.buckets.forEach(bib => (res[b.key][bib.key] = bib.doc_count));
        });
        return res;
      }
    });
}

export function getAppCountsByOneCondition(condition = "examiner_art_unit", byArtUnit = false) {
  const query = byArtUnit ? toESTripleGroupByQuery("examiner_art_unit", condition, "disposal_type", { ...DEFAULT_FILTER }, 500) : toESDoubleGroupByQuery(condition, "disposal_type", { ...DEFAULT_FILTER }, 500);
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", query))
    .then(resp => resp.json())
    .then(json => {
      if (byArtUnit) {
        let res = {};
        json.aggregations.examiner_art_unit.buckets.forEach(b => {
          res[b.key] = {};
          b[condition].buckets.forEach(bib => {
            res[b.key][bib.key] = {};
            bib.disposal_type.buckets.forEach(bibib => (res[b.key][bib.key][bibib.key] = bibib.doc_count));
          });
        });
        return res;
      } else {
        let res = {};
        json.aggregations[condition].buckets.forEach(b => {
          res[b.key] = {};
          b.disposal_type.buckets.forEach(bib => (res[b.key][bib.key] = bib.doc_count));
        });
        return res;
      }
    });
}

export function getDistinctValues(field, queries) {
  return fetch(PROSECUTION_ES_URL, optionsGen("POST", toESGroupByQuery(field, { ...DEFAULT_FILTER, ...queries }, 20), AUTH))
    .then(resp => resp.json())
    .then(json => {
      return json.aggregations[field].buckets.map(e => toTitleCase(e.key));
    });
}
